/*
 * hyperion-index:begin
 * ---
 * path: hyperion-fe/src/platform/observability/PostHogProvider.tsx
 * layer: web
 * area: providers
 * kind: source
 * web_root: providers
 * relpath: web/src/providers/PostHogProvider.tsx
 * filename: PostHogProvider.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://us.i.posthog.com';

let postHogPromise: Promise<typeof import('posthog-js').default> | null = null;

function loadPostHog(): Promise<typeof import('posthog-js').default> | null {
  if (!POSTHOG_KEY) return null;
  postHogPromise ??= import('posthog-js').then(({ default: posthog }) => {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      capture_pageview: false, // we capture manually on route change
      capture_pageleave: true,
      autocapture: true,
    });
    return posthog;
  });
  return postHogPromise;
}

/** Capture page views on route changes. */
function usePageView() {
  const location = useLocation();
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    if (!POSTHOG_KEY) return;
    void loadPostHog()?.then((posthog) => {
      posthog.capture('$pageview', { $current_url: window.location.href });
    });
    prevPath.current = location.pathname;
  }, [location.pathname]);
}

/** Identify the logged-in user with PostHog. Call when auth state changes. */
export function identifyUser(user: {
  id: string;
  email: string;
  name: string;
  role: string;
  tenantId: string;
}) {
  if (!POSTHOG_KEY) return;
  void loadPostHog()?.then((posthog) => {
    posthog.identify(user.id, {
      email: user.email,
      name: user.name,
      role: user.role,
      tenant_id: user.tenantId,
    });
  });
}

/** Reset PostHog identity on logout. */
export function resetPostHog() {
  if (!POSTHOG_KEY || !postHogPromise) return;
  void postHogPromise.then((posthog) => posthog.reset());
}

/** Capture an event without making analytics part of the startup bundle. */
export function capturePostHog(event: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return;
  void loadPostHog()?.then((posthog) => posthog.capture(event, properties));
}

/** Wraps children and captures page views. No-op when VITE_POSTHOG_KEY is empty. */
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  usePageView();
  return <>{children}</>;
}
